import React, { memo, useCallback, useRef } from 'react';
import { View, FlatList, Text, ActivityIndicator, StyleSheet } from 'react-native';
import { ADMIN_COLORS } from '@/constants/admin/colors';
import { SVG_COMPONENTS } from '@/assets/admin';
import { TagListItem } from './TagListItem';
import type { CorkBoardTag } from '@/types/residents/corkBoard';

interface TagListProps {
  tags: CorkBoardTag[];
  isLoading: boolean;
  onToggleEnabled: (tag: CorkBoardTag) => void;
  onEdit: (tag: CorkBoardTag) => void;
  onDelete: (tag: CorkBoardTag) => void;
  onMoveUp?: (tagId: number) => void;
  onMoveDown?: (tagId: number) => void;
  onRefresh?: () => void;
  isRefreshing?: boolean;
}

export const TagList: React.FC<TagListProps> = memo(
  ({ tags, isLoading, onToggleEnabled, onEdit, onDelete, onMoveUp, onMoveDown, onRefresh, isRefreshing }) => {
    const tagsLengthRef = useRef(tags.length);
    tagsLengthRef.current = tags.length;

    const renderItem = useCallback(
      ({ item, index }: { item: CorkBoardTag; index: number }) => (
        <TagListItem
          tag={item}
          isFirst={index === 0}
          isLast={index === tagsLengthRef.current - 1}
          onToggleEnabled={onToggleEnabled}
          onEdit={onEdit}
          onDelete={onDelete}
          onMoveUp={onMoveUp}
          onMoveDown={onMoveDown}
        />
      ),
      [onToggleEnabled, onEdit, onDelete, onMoveUp, onMoveDown],
    );

    const keyExtractor = useCallback((item: CorkBoardTag) => String(item.id), []);

    if (isLoading && tags.length === 0) {
      return (
        <View style={styles.centered}>
          <ActivityIndicator size="large" color={ADMIN_COLORS.PRIMARY} />
        </View>
      );
    }

    if (!isLoading && tags.length === 0) {
      return (
        <View style={styles.centered}>
          <SVG_COMPONENTS.EMPTY_MESSAGE width={48} height={48} color={ADMIN_COLORS.TEXT_TERTIARY} />
          <Text style={styles.emptyTitle}>No tags yet</Text>
          <Text style={styles.emptySubtitle}>
            Create your first tag to help residents categorize their posts.
          </Text>
        </View>
      );
    }

    return (
      <FlatList
        data={tags}
        renderItem={renderItem}
        keyExtractor={keyExtractor}
        style={styles.list}
        contentContainerStyle={styles.listContent}
        onRefresh={onRefresh}
        refreshing={isRefreshing ?? false}
      />
    );
  },
);

TagList.displayName = 'TagList';

const styles = StyleSheet.create({
  list: {
    flex: 1,
  },
  listContent: {
    flexGrow: 1,
    paddingTop: 8,
    paddingBottom: 8,
  },
  centered: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 32,
    minHeight: 200,
  },
  emptyIcon: {
    marginBottom: 12,
  },
  emptyTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: ADMIN_COLORS.TEXT.PRIMARY,
    marginBottom: 8,
  },
  emptySubtitle: {
    fontSize: 14,
    color: ADMIN_COLORS.TEXT.SECONDARY,
    textAlign: 'center',
    maxWidth: 300,
  },
});
